import React from 'react'
import ReactDOMServer from 'react-dom/server'
import Badge, { BADGE_TYPE } from '../Badge'
import { LABEL_TYPE } from '../Label'
import {
  FINDING_HTML_ID_ATTRIBUTE,
  OTHER_TITLE_ID,
  SEVERITY_ID,
  VULNERABILITY_STATUS_ID,
} from '../../constants'
import { isEmpty, sortBy, startCase } from 'lodash-es'

export const getOrderedFindings = reportContent => {
  const findings = reportContent?.breakdownAndAutomatedAnalysis?.vulnerabilitiesArray || []

  return reportContent?.breakdownAndAutomatedAnalysis?.sortBySeverity
    ? sortBy(findings, finding => parseInt(finding.severity?.value, 10))
    : findings
}

export const getFindingTitle = finding =>
  Number(finding?.title?.value) !== OTHER_TITLE_ID ? finding?.title?.label : finding?.otherTitle

export const hasReproductionTest = finding => !!finding?.reproductionTest?.code?.trim()

export const getReproductionTestId = findingNumber => `reproduction-test-${findingNumber}`

const REPRODUCTION_TEST_LANGUAGES = {
  cairo: 'Cairo',
  go: 'Go',
  js: 'JavaScript',
  jsx: 'JavaScript',
  move: 'Move',
  py: 'Python',
  rs: 'Rust',
  sol: 'Solidity',
  ts: 'TypeScript',
  tsx: 'TypeScript',
  vy: 'Vyper',
}

export const getReproductionTestLanguage = (fileName, reportContent) => {
  const extension = (fileName || '')
    .trim()
    .toLowerCase()
    .match(/\.([a-z0-9]+)$/)?.[1]

  if (REPRODUCTION_TEST_LANGUAGES[extension]) {
    return REPRODUCTION_TEST_LANGUAGES[extension]
  }

  const enabledLanguages = Object.keys(reportContent?.summary?.languages || {}).filter(
    language => reportContent.summary.languages[language],
  )

  return enabledLanguages.length === 1 ? enabledLanguages[0] : null
}

export const getMarkdownLanguage = language =>
  (language || '').toLowerCase().replace(/[^a-z0-9_+-]/g, '')

export const getCodeFence = code => {
  const longestBacktickRun = (code.match(/`+/g) || []).reduce(
    (longest, run) => Math.max(longest, run.length),
    0,
  )
  return '`'.repeat(Math.max(3, longestBacktickRun + 1))
}

export const getSeverityLabelType = severity => {
  switch (Number(severity)) {
    case SEVERITY_ID.HIGH:
      return LABEL_TYPE.DANGER
    case SEVERITY_ID.MEDIUM:
      return LABEL_TYPE.WARNING
    case SEVERITY_ID.LOW:
      return LABEL_TYPE.INFO
    case SEVERITY_ID.INFORMATIONAL:
      return LABEL_TYPE.SUCCESS
    default:
      return LABEL_TYPE.SECONDARY
  }
}

export const getStatusLabelType = status => {
  switch (Number(status)) {
    case VULNERABILITY_STATUS_ID.FIXED:
      return BADGE_TYPE.SUCCESS
    case VULNERABILITY_STATUS_ID.OPEN:
      return BADGE_TYPE.DANGER
    case VULNERABILITY_STATUS_ID.MITIGATED:
      return BADGE_TYPE.WARNING
    default:
      return BADGE_TYPE.INFO
  }
}

export const hasTools = tools => {
  if (isEmpty(tools)) {
    return false
  }

  for (let tool of tools) {
    if (tool.description) {
      return true
    }
  }

  return false
}

const appendClass = (attributes, additionalClass) => {
  const classRegex = /class="([^"]*)"/
  const classMatch = classRegex.exec(attributes)

  if (classMatch) {
    return attributes.replace(classRegex, `class="${classMatch[1]} ${additionalClass}"`)
  } else {
    return ` class="${additionalClass}"${attributes}`
  }
}

export const qsCodeBlockStyle = [
  {
    type: 'output',
    filter: text =>
      text.replace(/<pre([^>]*)><code([^>]*)>/gi, (match, preAttrs, codeAttrs) => {
        const newPreAttrs = appendClass(preAttrs, 'code-block')
        return `<pre${newPreAttrs}><code${codeAttrs}>`
      }),
  },
]

export const qsFindingId = prefix => [
  {
    type: 'output',
    regex: new RegExp(`${prefix}-([0-9]+)+`, 'ig'),
    replace: (match, finding) => `<a href="#${FINDING_HTML_ID_ATTRIBUTE}${finding}">${match}</a>`,
  },
]

export const qsBadge = [
  {
    type: 'output',
    regex: /qs-badge-(unresolved|fixed|acknowledged|mitigated)+/g,
    replace: (match, status) => {
      let type

      switch (status) {
        case 'fixed':
          type = BADGE_TYPE.SUCCESS
          break
        case 'unresolved':
          type = BADGE_TYPE.DANGER
          break
        case 'mitigated':
          type = BADGE_TYPE.WARNING
          break
        default:
          type = BADGE_TYPE.INFO
          break
      }

      return ReactDOMServer.renderToString(<Badge type={type}>{startCase(status)}</Badge>)
    },
  },
]
